import { GeoJson } from '@ais-ui/api-spec/shapes';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useToast,
} from '@ais-ui/ui-core';

import { useShapesDelete } from '@ais-ui/api';
import { useState } from 'react';
import { mutateCallbacks } from '../../utils/mutateCallbacks';
import { useLocalization } from '@ais-ui/locale-management';
import { useSettings } from '../../Settings/SettingsProvider';
import { useLayers } from '@ais-ui/react-openlayers';

interface DeleteShapeProps {
  shape: GeoJson;
  showDialog: boolean;
  setShowDialog: (show: boolean) => void;
}

export function DeleteShape({
  showDialog,
  setShowDialog,
  shape,
}: DeleteShapeProps) {
  const { messages } = useLocalization();
  const { doesLayerExist, removeLayerById } = useLayers();
  const { settings } = useSettings();
  const { mutate } = useShapesDelete(settings);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const onDelete = (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);

    const shapeName = shape.properties.name
      ? shape.properties.name
      : 'unknown shape';

    if (!shape.properties.id) {
      toast({
        className: 'delete-shape-error-toast',
        title: 'Error!',
        description: messages.shapesDeleteFailed.replace('{label}', shapeName),
        variant: 'destructive',
      });
      setShowDialog(false);
      return;
    }

    const callBacks = mutateCallbacks(
      toast,
      messages,
      'delete',
      'shape',
      setIsSubmitting,
      setShowDialog
    );

    const extendedOnSuccess = () => {
      callBacks.onSuccess();
    };

    const extendedCallbacks = {
      ...callBacks,
      onSuccess: extendedOnSuccess,
    };

    if (doesLayerExist(shape.properties.id)) {
      removeLayerById(shape.properties.id);
    }

    mutate({ id: shape.properties.id }, extendedCallbacks);
  };

  // Function to truncate long shape names
  const truncateName = (name: string, maxLength: number = 30) => {
    if (!name) return 'unknown shape';
    return name.length > maxLength
      ? `${name.substring(0, maxLength)}...`
      : name;
  };

  const shapeName = shape.properties.name || 'unknown shape';

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent id="mutate-shape-dialog">
        <DialogHeader className="space-y-4">
          <DialogTitle>{messages.shapesDeleteTitle}</DialogTitle>
          <DialogDescription>{messages.shapesDeleteWarning}</DialogDescription>
        </DialogHeader>
        <form onSubmit={onDelete}>
          <div className="grid grid-cols-[auto_minmax(50%,_1fr)] gap-y-4 gap-x-2 items-baseline">
            <Label className="font-light">
              {messages.shapesDeleteShapeName}
            </Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Label htmlFor="shape-name" className="truncate">
                    {truncateName(shapeName)}
                  </Label>
                </TooltipTrigger>
                {shapeName.length > 30 && (
                  <TooltipContent>
                    <p>{shapeName}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
          <DialogFooter className="p-4">
            <Button
              disabled={isSubmitting}
              variant="destructive"
              id="shape-confirm-delete-button"
            >
              {isSubmitting
                ? messages.genericSubmitting
                : messages.genericSubmit}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
